/**
 * Dynamic role display config (label, icon) from the login-loaded catalog.
 */

import * as React from 'react';
import { useRoleCatalogStore } from '@/stores/roleCatalogStore';
import { getRoleLabel } from '@/utils/role-label-utils';
import type { Role } from '@/types/role.types';
import { Circle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import * as icons from 'lucide-react';

/** Display config for a user role (from the login catalog). */
export interface UserRoleConfig {
  label: string;
  labelShort?: string;
  icon: LucideIcon;
  colorClass?: string;
}

/**
 * Resolves a Lucide icon component from a role's `icon` string (e.g. 'Shield').
 * Falls back to {@link Circle} if the icon is not found.
 */
export function resolveLucideIcon(iconName: string | null | undefined): LucideIcon {
  if (!iconName) return Circle;
  const resolved = (icons as Record<string, unknown>)[iconName];
  return (resolved as LucideIcon) ?? Circle;
}

/**
 * Builds a UserRoleConfig-compatible object from a dynamic Role.
 */
export function roleToConfig(role: Role): UserRoleConfig {
  return {
    label: role.label,
    labelShort: role.label,
    icon: resolveLucideIcon(role.icon),
    // `colorClass` is the raw token from the role; CSS-class consumers should read `role.color`.
    colorClass: role.color ?? undefined,
  };
}

/**
 * Returns a role config map from the login catalog (keyed by UPPERCASE role code).
 */
export const useRoleConfig = (): Record<string, UserRoleConfig> => {
  const roles = useRoleCatalogStore((state) => state.roles);

  return React.useMemo(() => {
    const merged: Record<string, UserRoleConfig> = {};

    for (const role of roles) {
      merged[role.code.toUpperCase()] = roleToConfig(role);
    }

    return merged;
  }, [roles]);
};

/**
 * Resolves a role label from the login catalog.
 */
export const useRoleLabel = (roleCode: string | null | undefined): string => {
  useRoleCatalogStore((state) => state.roles);
  if (!roleCode) return 'Inconnu';
  return getRoleLabel(roleCode);
};

/**
 * Resolves a role icon from the dynamic config.
 */
export const useRoleIcon = (roleCode: string | null | undefined): LucideIcon => {
  const config = useRoleConfig();
  if (!roleCode) return Circle;
  return config[roleCode.toUpperCase()]?.icon ?? Circle;
};
